import { observable, action, toJS, computed, spy } from 'mobx';

export default class DealMapperViewStore {
  @observable hoveredElement = null;
  @observable highlightedDealTransactions = [];
  @observable highlightedDtvts = [];
  @observable highlightedVoucherTransactions = [];
  @observable dealCentric = false;
  @observable voucherCentric = false;
  @observable dealTransactionRefs = [];
  @observable voucherTransactionRefs = [];

  constructor(dealMapperStore) {
    this.dealMapperStore = dealMapperStore;
    // use this ^ if you need to get info from the dealMapperStore
  }

  removeDupes = arr => [...new Set(arr)];

  @action pushDealTransactionRef(ref, dealTransactionId, sectionId) {
    this.dealTransactionRefs = [
      ...this.dealTransactionRefs,
      { ref, dealTransactionId, sectionId },
    ];
  }

  @action removeDealTransactionRef(dealTransactionId, sectionId) {
    this.dealTransactionRefs = this.dealTransactionRefs.filter(dr => {
      if (sectionId === dr.sectionId) {
        if (dealTransactionId === dr.dealTransactionId) {
          return false;
        }
      }

      return true;
    });
  }

  @action pushVoucherTransactionRef(ref, voucherTransactionId, sectionId) {
    this.voucherTransactionRefs = [
      ...this.voucherTransactionRefs,
      { ref, voucherTransactionId, sectionId },
    ];
  }

  @action removeVoucherTransactionRef(voucherTransactionId, sectionId) {
    this.voucherTransactionRefs = this.voucherTransactionRefs.filter(vr => {
      if (sectionId === vr.sectionId) {
        if (voucherTransactionId === vr.voucherTransactionId) {
          return false;
        }
      }

      return true;
    });
  }

  getSectionsDealCentric = () => {
    const sections = [];
    this.dealMapperStore.deals.forEach((d, index) => {
      const deals = [];
      const dtvts = [];
      const vouchers = [];

      const dealTransactionsOnDeal = this.dealMapperStore
        .getDealTransactionsOnDeal(d)
        .map(dt => ({ ...dt, sectionId: index }));
      deals.push({
        ...d,
        dealTransactions: dealTransactionsOnDeal,
      });

      const dtvtsOnDeal = dealTransactionsOnDeal
        .reduce((agg, dtOnDeal) => {
          return [
            ...agg,
            ...this.dealMapperStore.getDtvtsOnDealTransaction(dtOnDeal),
          ];
        }, [])
        .map(dtOnDeal => ({ ...dtOnDeal, sectionId: index }));

      const voucherTransactionsOnDeal = this.removeDupes(
        dtvtsOnDeal
          .map(dtvtOnDeal =>
            this.dealMapperStore.getVoucherTransactionOnDtvt(dtvtOnDeal),
          )
          .filter(dtvtOnDeal => dtvtOnDeal)
          .filter(
            (v, x, a) =>
              a.findIndex(t => t.VoucherTranId === v.VoucherTranId) === x,
          ),
      ).map(itOnDeal => ({ ...itOnDeal, sectionId: index }));

      // The set stuff removes duplicates
      const vouchersOnDeal = this.removeDupes(
        voucherTransactionsOnDeal.map(itOnDeal =>
          this.dealMapperStore.getVoucherOnVoucherTransaction(itOnDeal),
        ),
      );

      const compositeVouchersOnDeal = vouchersOnDeal.map(iOnDeal => {
        const vtsOnVoucher = voucherTransactionsOnDeal.filter(
          vt => vt.VoucherId === iOnDeal.VoucherId,
        );

        return {
          ...iOnDeal,
          voucherTransactions: vtsOnVoucher,
        };
      });

      vouchers.push(...compositeVouchersOnDeal);

      const dtvtsWithRefs = this.getDtvtsWithRefs(dtvtsOnDeal);

      dtvts.push(...dtvtsWithRefs);

      sections.push({
        deals,
        dtvts,
        vouchers,
        sectionId: index,
      });
    });

    return sections;
  };

  getSectionsVoucherCentric = () => {
    const sections = [];
    this.dealMapperStore.vouchers.forEach((i, index) => {
      const deals = [];
      const dtvts = [];
      const vouchers = [];

      const vtsOnVoucher = this.dealMapperStore
        .getVoucherTransactionsOnVoucher(i)
        .map(it => ({ ...it, sectionId: index }));

      vouchers.push({
        ...i,
        voucherTransactions: vtsOnVoucher,
      });

      const dtvtsOnVoucher = this.dealMapperStore
        .getAllDtvtsOnVoucherTransactions(vtsOnVoucher)
        .map(dtvt => ({ ...dtvt, sectionId: index }));

      const dtsOnVoucher = this.removeDupes(
        dtvtsOnVoucher
          .map(dtvt => this.dealMapperStore.getDealTransactionOnDtvt(dtvt))
          // filter out undefined stuff
          .filter(d => d)
          .filter(
            (v, x, a) => a.findIndex(t => t.DealTranId === v.DealTranId) === x,
          )
          .map(dt => ({ ...dt, sectionId: index })),
      );

      const dealsOnVoucher = [
        ...new Set(
          dtsOnVoucher.map(dt =>
            this.dealMapperStore.getDealOnDealTransaction(dt),
          ),
        ),
      ];

      const compositeDealsOnVoucher = dealsOnVoucher.map(dOnI => {
        return {
          ...dOnI,
          dealTransactions: dtsOnVoucher.filter(
            dtOnVoucher => dtOnVoucher.DealId === dOnI.DealId,
          ),
        };
      });

      deals.push(...compositeDealsOnVoucher);

      const dtvtsWithRefs = this.getDtvtsWithRefs(dtvtsOnVoucher, index);

      dtvts.push(...dtvtsWithRefs);

      sections.push({
        deals,
        dtvts,
        vouchers,
        sectionId: index,
      });
    });

    return sections;
  };

  getDtvtsWithRefs = dtvts => {
    return dtvts.reduce((agg, dtvt) => {
      try {
        const dealTransactionRef = this.dealTransactionRefs.find(
          dr =>
            dtvt.DealTranId === dr.dealTransactionId &&
            dr.sectionId === dtvt.sectionId,
        ).ref;
        const voucherTransactionRef = this.voucherTransactionRefs.find(
          ir =>
            dtvt.VoucherTranId === ir.voucherTransactionId &&
            ir.sectionId === dtvt.sectionId,
        ).ref;
        return [
          ...agg,
          {
            ...dtvt,
            dealTransactionRef,
            voucherTransactionRef,
          },
        ];
      } catch {
        return agg;
      }
    }, []);
  };

  getSectionsOpen = () => {
    const sections = [];
    const sortedDeals = [];
    const sortedVouchers = [];

    const isVoucherSorted = voucher =>
      !!sortedVouchers.find(v => v.VoucherId === voucher.VoucherId);

    const isDealSorted = deal =>
      !!sortedDeals.find(d => d.DealId === deal.DealId);

    const findNextUnsortedDeal = () =>
      this.dealMapperStore.deals.find(d => !isDealSorted(d));

    const findNextUnsortedVoucher = () =>
      this.dealMapperStore.vouchers.find(i => !isVoucherSorted(i));

    const getAllConnectedItems = (
      startingObject,
      objectType,
      sectionId,
      agg = {
        deals: [],
        vouchers: [],
        dtvts: [],
      },
    ) => {
      if (!startingObject) return;
      if (objectType === 'deal') {
        if (isDealSorted(startingObject)) return;

        sortedDeals.push(startingObject);

        agg.deals.push({
          ...startingObject,
          dealTransactions: this.dealMapperStore
            .getDealTransactionsOnDeal(startingObject)
            .map(dt => ({ ...dt, sectionId })),
        });
        agg.dtvts.push(
          ...this.dealMapperStore
            .getDtvtsOnDeal(startingObject)
            .map(dtvt => ({ ...dtvt, sectionId })),
        );

        const vouchers = this.dealMapperStore.getVouchersOnDeal(startingObject);
        vouchers.forEach(i =>
          getAllConnectedItems(i, 'voucher', sectionId, agg),
        );
      } else {
        if (isVoucherSorted(startingObject)) return;
        sortedVouchers.push(startingObject);

        agg.vouchers.push({
          ...startingObject,
          voucherTransactions: this.dealMapperStore
            .getVoucherTransactionsOnVoucher(startingObject)
            .map(it => ({ ...it, sectionId })),
        });
        agg.dtvts.push(
          ...this.dealMapperStore
            .getDtvtsOnVoucher(startingObject)
            .map(dtvt => ({ ...dtvt, sectionId })),
        );

        const deals = this.dealMapperStore.getDealsOnVoucher(startingObject);
        deals.forEach(d => getAllConnectedItems(d, 'deal', sectionId, agg));
      }

      agg.dtvts = this.getDtvtsWithRefs(agg.dtvts, 0);

      return {
        deals: [...new Set(agg.deals)],
        dtvts: [...new Set(agg.dtvts)],
        vouchers: [...new Set(agg.vouchers)],
      };
    };
    let keepSorting = true;
    let counter = 0;
    while (keepSorting) {
      if (counter > 50) {
        keepSorting = false;
      }
      const dealToSort = findNextUnsortedDeal();
      const voucherToSort = findNextUnsortedVoucher();

      if (dealToSort) {
        sections.push(getAllConnectedItems(dealToSort, 'deal', counter));
      } else if (voucherToSort) {
        sections.push(getAllConnectedItems(voucherToSort, 'voucher', counter));
      } else {
        keepSorting = false;
      }
      counter++;
    }

    return sections;
  };

  @computed get dealMapForView() {
    try {
      if (this.dealCentric) return this.getSectionsDealCentric();
      if (this.voucherCentric) return this.getSectionsVoucherCentric();
      if (!this.dealCentric && !this.voucherCentric)
        return this.getSectionsOpen();
    } catch (e) {
      console.error(e);
    }
    return [];
  }

  @action setViewCentricity({ dealCentric, voucherCentric }) {
    if (dealCentric) {
      this.dealCentric = true;
      this.voucherCentric = false;
    } else if (voucherCentric) {
      this.voucherCentric = true;
      this.dealCentric = false;
    } else if (!dealCentric && !voucherCentric) {
      this.voucherCentric = false;
      this.dealCentric = false;
    } else {
      throw new Error(
        'must be dealCentric, voucherCentric, or neither but CANNOT BE BOTH',
      );
    }
  }

  @action setHoveredElement(element) {
    this.hoveredElement = element;

    if (!element) {
      this.highlightedVoucherTransactions = [];
      this.highlightedDealTransactions = [];
      this.highlightedDtvts = [];
      return;
    }

    if (this.hoveredElement.DealTranId && this.hoveredElement.VoucherTranId) {
      // dtvt
      this.highlightedDtvts.push(this.hoveredElement);

      const voucherTransaction = this.dealMapperStore.voucherTransactions.find(
        it => it.VoucherTranId === this.hoveredElement.VoucherTranId,
      );

      const dealTransaction = this.dealMapperStore.dealTransactions.find(
        dt => dt.DealTranId === this.hoveredElement.DealTranId,
      );

      if (dealTransaction) {
        this.highlightedDealTransactions.push(dealTransaction);
      }

      if (voucherTransaction) {
        this.highlightedVoucherTransactions.push(voucherTransaction);
      }
    } else if (this.hoveredElement.DealTranId) {
      // dt
      this.highlightedDealTransactions.push(this.hoveredElement);

      const dtvts = this.dealMapperStore.getDtvtsOnDealTransaction(
        this.hoveredElement,
      );
      const voucherTransactions = dtvts.map(dtvt =>
        this.dealMapperStore.getVoucherTransactionOnDtvt(dtvt),
      );

      this.highlightedDtvts = dtvts;
      this.highlightedVoucherTransactions = voucherTransactions;
    } else if (this.hoveredElement.VoucherTranId) {
      // it
      this.highlightedVoucherTransactions.push(this.hoveredElement);

      const dtvts = this.dealMapperStore.getDtvtsOnVoucherTransaction(
        this.hoveredElement,
      );

      const dealTransaction = dtvts.map(dtvt =>
        this.dealMapperStore.getDealTransactionOnDtvt(dtvt),
      );

      this.highlightedDtvts = dtvts;
      this.highlightedDealTransactions = dealTransaction;
    } else if (this.hoveredElement.VoucherId) {
      // voucher

      const voucherTransactions = this.dealMapperStore.getVoucherTransactionsOnVoucher(
        this.hoveredElement,
      );
      const dtvts = voucherTransactions.reduce((agg, it) => {
        const dtvtsOnIt = this.dealMapperStore.getDtvtsOnVoucherTransaction(it);
        if (dtvtsOnIt) {
          agg.push(...dtvtsOnIt);
        }
        return agg;
      }, []);
      const dealTransactions = dtvts.map(dtvt =>
        this.dealMapperStore.getDealTransactionOnDtvt(dtvt),
      );
      this.highlightedDtvts = dtvts;
      this.highlightedDealTransactions = dealTransactions;
      this.highlightedVoucherTransactions = voucherTransactions;
    } else if (this.hoveredElement.DealId) {
      // deal

      const dealTransactions = this.dealMapperStore.getDealTransactionsOnDeal(
        this.hoveredElement,
      );

      const dtvts = dealTransactions.reduce((agg, dt) => {
        const dtvtsOnDt = this.dealMapperStore.getDtvtsOnDealTransaction(dt);
        if (dtvtsOnDt) {
          agg.push(...dtvtsOnDt);
        }
        return agg;
      }, []);

      const voucherTransactions = dtvts.map(dtvt =>
        this.dealMapperStore.getVoucherTransactionOnDtvt(dtvt),
      );

      this.highlightedDtvts = dtvts;
      this.highlightedVoucherTransactions = voucherTransactions;
      this.highlightedDealTransactions = dealTransactions;
    }
  }
}
